<template>
    <h3 v-if="triggerLabel">
        <span>{{ triggerLabel }}</span>
    </h3>
    <p class="sf__filters__tooltip__trigger">
        <button
            ref="tooltip"
            type="button"
            @click.stop="toggle"
            @mouseenter="openTooltip"
            @mouseleave="scheduleClose"
            @focus="openTooltip"
            @blur="scheduleClose"
            :aria-expanded="opened.toString()"
            aria-label="Need Help?"
            title="Need Help?"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                <g clip-path="url(#clip0_filter_tooltip_info)">
                    <path d="M7 14C8.85652 14 10.637 13.2625 11.9497 11.9497C13.2625 10.637 14 8.85652 14 7C14 5.14348 13.2625 3.36301 11.9497 2.05025C10.637 0.737498 8.85652 0 7 0C5.14348 0 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 8.85652 0.737498 10.637 2.05025 11.9497C3.36301 13.2625 5.14348 14 7 14ZM7.81375 5.7645L6.93875 9.88138C6.8775 10.1789 6.96413 10.3477 7.20475 10.3477C7.3745 10.3477 7.63088 10.2865 7.805 10.1325L7.728 10.4965C7.47687 10.7993 6.923 11.0197 6.44613 11.0197C5.831 11.0197 5.56938 10.6505 5.73913 9.86562L6.38487 6.83113C6.44088 6.57475 6.39013 6.482 6.13375 6.41988L5.73913 6.349L5.81087 6.01562L7.81375 5.7645ZM7 4.8125C6.76794 4.8125 6.54538 4.72031 6.38128 4.55622C6.21719 4.39212 6.125 4.16956 6.125 3.9375C6.125 3.70544 6.21719 3.48288 6.38128 3.31878C6.54538 3.15469 6.76794 3.0625 7 3.0625C7.23206 3.0625 7.45462 3.15469 7.61872 3.31878C7.78281 3.48288 7.875 3.70544 7.875 3.9375C7.875 4.16956 7.78281 4.39212 7.61872 4.55622C7.45462 4.72031 7.23206 4.8125 7 4.8125Z" fill="black"/>
                </g>
                <defs>
                    <clipPath id="clip0_filter_tooltip_info">
                        <rect width="14" height="14" fill="white"/>
                    </clipPath>
                </defs>
            </svg>
        </button>
    </p>
    <teleport to="body">
        <transition name="fade">
            <div
                class="sf__filters__tooltip"
                v-if="opened"
                ref="tooltipPanel"
                role="tooltip"
                :style="tooltipStyle"
                @click.self="opened = false"
                @mouseenter="cancelClose"
                @mouseleave="scheduleClose"
            >
                <div class="sf__filters__tooltip__inner">
                    <button type="button" class="sf__filters__tooltip__close" @click="opened = false" aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.75 6.31L17.69 5.25L12 10.94L6.31 5.25L5.25 6.31L10.94 12L5.25 17.69L6.31 18.75L12 13.06L17.69 18.75L18.75 17.69L13.06 12L18.75 6.31Z" fill="#0E131B"/>
                        </svg>
                    </button>
                    <h5 class="sf__filters__tooltip__title">{{ tooltipTitle }}</h5>
                    <div
                        v-if="content"
                        class="sf__filters__tooltip__description"
                        v-html="content"
                    ></div>
                    <p v-else-if="filter?.description" class="sf__filters__tooltip__description">{{ filter.description }}</p>
                    <button
                        v-if="videoId"
                        type="button"
                        class="sf__filters__tooltip__video"
                        @click.stop="openVideo"
                        :aria-label="`Watch ${tooltipTitle}`"
                    >
                        <img :src="`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`" :alt="tooltipTitle">
                        <span class="sf__filters__tooltip__play" aria-hidden="true"></span>
                    </button>
                </div>
            </div>
        </transition>
        <div class="sf__filters__tooltip__lightbox" v-if="videoModalOpen">
            <InstallVideoModal :video="video" @close="videoModalOpen = false" />
        </div>
    </teleport>
</template>

<script>
import InstallVideoModal from './InstallVideoModal.vue';

export default {

    props: ['filter', 'results', 'title', 'content', 'triggerLabel'],

    data() {
        return {
            opened: false,
            tooltipStyle: {},
            closeTimer: null,
            videoModalOpen: false
        }
    },

    components: {
        InstallVideoModal
    },

    computed: {
        videoId() {
            return this.getVideoId(this.filter?.video_url);
        },

        video() {
            return {
                id: this.videoId,
                video: this.filter.video_url,
                title: this.tooltipTitle
            };
        },

        tooltipTitle() {
            if (this.title) {
                return this.title;
            }

            return `Learn more about ${this.filter?.label || 'this option'}`;
        }
    },

    mounted() {
        document.addEventListener('click', this.closeOnOutsideClick);
        document.addEventListener('keydown', this.closeOnEscape, true);
        window.addEventListener('resize', this.positionTooltip);
        window.addEventListener('scroll', this.positionTooltip, true);
    },

    beforeUnmount() {
        this.cancelClose();
        document.removeEventListener('click', this.closeOnOutsideClick);
        document.removeEventListener('keydown', this.closeOnEscape, true);
        window.removeEventListener('resize', this.positionTooltip);
        window.removeEventListener('scroll', this.positionTooltip, true);
    },

    methods: {
        toggle() {
            this.cancelClose();
            this.opened = window.innerWidth <= 479 ? !this.opened : true;

            if (this.opened) {
                this.$nextTick(this.positionTooltip);
            }
        },

        openTooltip() {
            if (window.innerWidth <= 479) return;

            this.cancelClose();
            this.opened = true;
            this.$nextTick(this.positionTooltip);
        },

        scheduleClose() {
            if (window.innerWidth <= 479 || this.videoModalOpen) return;

            this.cancelClose();
            this.closeTimer = window.setTimeout(() => {
                this.opened = false;
            }, 250);
        },

        cancelClose() {
            if (!this.closeTimer) return;

            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
        },

        openVideo() {
            this.cancelClose();
            this.videoModalOpen = true;
        },

        positionTooltip() {
            if (!this.opened || !this.$refs.tooltip) return;

            if (window.innerWidth <= 479) {
                this.tooltipStyle = {};
                return;
            }

            const rect = this.$refs.tooltip?.getBoundingClientRect();
            if (!rect) return;

            const viewportPadding = 16;
            const width = Math.min(312, window.innerWidth - viewportPadding * 2);
            const panelHeight = this.$refs.tooltipPanel?.getBoundingClientRect().height || 360;
            const drawer = this.$refs.tooltip?.closest?.('.seatshop-vehicle-drawer');
            const drawerPanelRect = drawer
                ?.querySelector('.seatshop-vehicle-drawer__panel')
                ?.getBoundingClientRect();
            const drawerLeft = drawerPanelRect?.left ?? null;
            const left = drawerLeft === null
                ? Math.min(
                    Math.max(viewportPadding, rect.left + (rect.width / 2) - (width / 2)),
                    window.innerWidth - width - viewportPadding
                )
                : Math.max(viewportPadding, drawerLeft - width);
            const maximumTop = window.innerHeight - viewportPadding - panelHeight;
            const centeredTop = rect.top + (rect.height / 2) - (panelHeight / 2);
            const top = Math.max(
                viewportPadding,
                Math.min(centeredTop, Math.max(viewportPadding, maximumTop))
            );

            this.tooltipStyle = {
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`
            };
        },

        closeOnOutsideClick(event) {
            if (!this.opened) return;
            if (this.$refs.tooltip?.contains(event.target) || this.$refs.tooltipPanel?.contains(event.target)) return;

            this.opened = false;
        },

        closeOnEscape(event) {
            if (event.key !== 'Escape') return;

            if (this.videoModalOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.videoModalOpen = false;
                return;
            }

            if (this.opened) {
                event.preventDefault();
                event.stopPropagation();
                this.opened = false;
            }
        },

        getVideoId(url) {
            // return if url is empty
            if (!url) return null;

            if (url.includes('youtu.be')) {
                return url.split('/').pop();
            } else if (url.includes('youtube.com')) {
                const urlParams = new URLSearchParams(new URL(url).search);
                return urlParams.get('v');
            }
        }
    }
}</script>
